import { useEffect, useRef, useState } from "react";
import { Camera, X, Upload as UploadIcon } from "lucide-react";
import api from "@/lib/api";
import AuthImg from "@/components/AuthImg";
import { toast } from "sonner";

/**
 * Reusable screenshot uploader with click, drag-drop and clipboard paste support.
 * Props:
 *   value: string[] (storage paths)
 *   onChange: (next: string[]) => void
 *   pasteScope?: "global" | "focused" (default "focused") - "global" listens on window while mounted
 *   testIdPrefix?: string
 */
export default function ScreenshotUploader({ value = [], onChange, pasteScope = "focused", testIdPrefix = "uploader" }) {
  const fileRef = useRef();
  const zoneRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadOne = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return null;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.path;
    } catch {
      toast.error(`Upload failed: ${file.name || "image"}`);
      return null;
    }
  };

  const uploadMany = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type?.startsWith("image/"));
    if (list.length === 0) return;
    setUploading(true);
    const paths = [];
    for (const f of list) {
      const p = await uploadOne(f);
      if (p) paths.push(p);
    }
    if (paths.length > 0) {
      onChange([...(value || []), ...paths]);
      toast.success(`${paths.length} screenshot${paths.length > 1 ? "s" : ""} uploaded`);
    }
    setUploading(false);
  };

  const onFilePick = (e) => {
    uploadMany(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    uploadMany(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) setDragging(false);
  };

  useEffect(() => {
    const el = pasteScope === "global" ? window : zoneRef.current;
    if (!el) return;
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        uploadMany(files);
      }
    };
    el.addEventListener("paste", handler);
    return () => el.removeEventListener("paste", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteScope, value]);

  const remove = (p) => onChange((value || []).filter((x) => x !== p));

  return (
    <div
      ref={zoneRef}
      tabIndex={0}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      data-testid={`${testIdPrefix}-zone`}
      className={`relative outline-none rounded-sm border border-dashed transition-colors ${
        dragging ? "border-blue-500 bg-blue-500/5" : "border-transparent"
      }`}
    >
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFilePick} className="hidden" data-testid={`${testIdPrefix}-input`} />

      <div className="flex flex-wrap gap-3 py-1">
        {(value || []).map((s) => (
          <div key={s} className="relative aspect-video w-48 border border-zinc-800 rounded-sm overflow-hidden bg-black">
            <AuthImg path={s} className="w-full h-full object-contain" />
            <button type="button" onClick={() => remove(s)} data-testid={`${testIdPrefix}-remove-${s}`} className="absolute top-1 right-1 bg-black/70 text-white rounded-sm p-1 hover:bg-rose-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className="aspect-video w-48 border border-dashed border-zinc-800 rounded-sm flex flex-col items-center justify-center text-zinc-500 hover:border-blue-500 hover:text-blue-500 text-xs gap-1.5 disabled:opacity-50 font-mono uppercase tracking-wider"
        >
          {uploading ? (<><UploadIcon className="w-5 h-5 animate-pulse" />Uploading</>) : (<><Camera className="w-5 h-5" />Upload</>)}
        </button>
      </div>

      <p className="text-[10px] text-zinc-600 font-mono mt-1 pl-0.5">
        Click, drag-drop, or paste (⌘/Ctrl+V) images anywhere here
      </p>

      {dragging && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-500/10 border-2 border-dashed border-blue-500 rounded-sm pointer-events-none">
          <span className="text-blue-400 font-mono text-xs uppercase tracking-wider">Drop to upload</span>
        </div>
      )}
    </div>
  );
}
